@layer refine.base {
  :host {
    --_day-selected-bg: var(--date-picker-day-selected-bg, var(--_theme-focus));
    --_day-today-color: var(--date-picker-day-today-color, var(--_theme-focus));
    --_day-outside-opacity: var(--date-picker-day-outside-opacity, 0.35);
    --_bg: var(--date-picker-bg, var(--color-canvas));
    --_border-color: var(--date-picker-border-color, var(--color-contrast-200));
    --_radius: var(--date-picker-radius, var(--rounded-xl));

    display: inline-block;
    min-width: var(--date-picker-min-width, 12rem);
  }

  :host([fullwidth]) {
    display: block;
  }

  :host([color='secondary']) {
    --_day-selected-bg: var(--date-picker-day-selected-bg, var(--_theme-base));
    --_day-today-color: var(--date-picker-day-today-color, var(--_theme-base));
  }

  /* Make the trigger look like a button — ore-input is readonly so pointer changes to text by
     default. `width: 100%` matters just as much — see ore-select's own `.trigger` for why:
     `:host` is `display: inline-block`, which never stretches its own children to fill its own
     box the way a flex/grid container with `stretch` alignment would. */
  .trigger {
    width: 100%;
    cursor: pointer;
  }

  .trigger::part(input) {
    cursor: pointer;
    caret-color: transparent;
    user-select: none;
  }

  /* ── Calendar popup ──────────────────────────────────────────────────────── */

  /* `fixed` (not `absolute`) — positioned via JS-computed top/left (see the positioner in
     date-picker.ts), same as ore-select's own `.dropdown`. Laid out relative to the viewport,
     so it never affects any ancestor's box size or scrollable-overflow area — `absolute`
     positioned relative to the host did, once the host sat near a scroll/clip ancestor's edge. */
  .calendar {
    position: fixed;
    inset: 0 auto auto 0;
    z-index: var(--z-dropdown, 8000);
    display: flex;
    flex-direction: column;
    gap: var(--size-2);
    min-width: 18rem;
    padding: var(--size-3);
    pointer-events: none;
    background: var(--_bg);
    border: var(--border) solid var(--_border-color);
    border-radius: var(--_radius);
    box-shadow: var(--date-picker-shadow, var(--shadow-lg));

    /* Hidden by default */
    opacity: 0;
    backdrop-filter: blur(var(--blur-sm));
    transform: translateY(var(--overlay-enter-translate-y));
    transition: var(
      --_motion-transition,
      opacity var(--transition-fast),
      transform var(--transition-fast),
      display var(--transition-fast) allow-discrete
    );
  }

  .calendar[data-open] {
    pointer-events: auto;
    opacity: 1;
    transform: translateY(0);

    @starting-style {
      opacity: 0;
      transform: translateY(var(--overlay-enter-translate-y));
    }
  }

  /* ── Calendar header ─────────────────────────────────────────────────────── */

  .cal-header {
    display: flex;
    gap: var(--size-1);
    align-items: center;
    justify-content: space-between;
    padding-block-end: var(--size-2);
    border-block-end: var(--border) solid var(--_border-color);
  }

  .nav-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--size-8);
    height: var(--size-8);
    padding: 0;
    color: var(--color-contrast-600);
    cursor: pointer;
    outline: none;
    background: transparent;
    border: none;
    border-radius: var(--rounded-md);
    transition:
      background var(--transition-fast),
      color var(--transition-fast);
  }

  .nav-btn:hover {
    color: var(--color-contrast-900);
    background: var(--color-contrast-300);
  }

  .nav-btn:focus-visible {
    box-shadow: 0 0 0 2px var(--_day-selected-bg);
  }

  .cal-label-btn {
    flex: 1;
    padding: var(--size-1) var(--size-2);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-contrast-900);
    text-align: center;
    cursor: pointer;
    outline: none;
    background: transparent;
    border: none;
    border-radius: var(--rounded-md);
    transition: background var(--transition-fast);
  }

  .cal-label-btn:hover {
    background: var(--color-contrast-300);
  }

  .cal-label-btn:focus-visible {
    box-shadow: 0 0 0 2px var(--_day-selected-bg);
  }

  .cal-label-month,
  .cal-label-year {
    display: inline-block;
  }

  .cal-label-sep {
    display: inline-block;
    padding-inline: var(--size-1);
    font-weight: var(--font-normal);
    color: var(--color-contrast-400);
  }

  /* ── Day grid ────────────────────────────────────────────────────────────── */

  /* Prevent display:flex from overriding the [hidden] attribute */
  [hidden] {
    display: none;
  }

  .cal-grid {
    display: flex;
    flex-direction: column;
    gap: var(--size-0-5);
  }

  .cal-grid-days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: var(--size-0-5);
    min-height: calc(7 * var(--size-10) + 6 * var(--size-0-5));
  }

  /* role="row" wrappers exist for ARIA grid structure only — `display: contents`
     removes them from layout so their children still participate directly in the
     parent's CSS grid tracks. */
  .cal-grid [role='row'] {
    display: contents;
  }

  .cal-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-sm);
    cursor: pointer;
    outline: none;
    border-radius: var(--rounded-md);
    transition:
      background var(--transition-fast),
      color var(--transition-fast);
  }

  .cal-cell-head {
    height: var(--size-8);
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
    color: var(--color-contrast-500);
    cursor: default;
  }

  .cal-cell-day {
    height: var(--size-8);
    color: var(--color-contrast-800);
  }

  .cal-cell-day:hover:not([data-disabled]) {
    color: var(--color-contrast-900);
    background: color-mix(in oklch, var(--_day-selected-bg) 15%, transparent);
  }

  .cal-cell-day:focus-visible {
    box-shadow: 0 0 0 2px var(--_day-selected-bg);
  }

  .cal-cell-day[data-today] {
    font-weight: var(--font-semibold);
    color: var(--_day-today-color);
  }

  .cal-cell-day[data-selected] {
    font-weight: var(--font-semibold);
    color: var(--color-white, #fff);
    background: var(--_day-selected-bg);
  }

  .cal-cell-day[data-outside] {
    opacity: var(--_day-outside-opacity);
  }

  .cal-cell-day[data-disabled] {
    pointer-events: none;
    cursor: not-allowed;
    opacity: 0.3;
  }

  /* ── Month / Year grids ──────────────────────────────────────────────────── */

  .cal-grid-months,
  .cal-grid-years {
    /* 4-column grid — same width as cal-grid-days, natural height. */
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--size-1);
  }

  .cal-cell-month,
  .cal-cell-year {
    height: var(--size-10);
    font-size: var(--text-sm);
    color: var(--color-contrast-800);
    cursor: pointer;
    border-radius: var(--rounded-md);
  }

  .cal-cell-month:hover:not([data-disabled]),
  .cal-cell-year:hover:not([data-disabled]) {
    background: color-mix(in oklch, var(--_day-selected-bg) 15%, transparent);
  }

  .cal-cell-month:focus-visible,
  .cal-cell-year:focus-visible {
    box-shadow: 0 0 0 2px var(--_day-selected-bg);
  }

  .cal-cell-month[data-selected],
  .cal-cell-year[data-selected] {
    font-weight: var(--font-semibold);
    color: var(--color-white, #fff);
    background: var(--_day-selected-bg);
  }

  .cal-cell-month[data-disabled],
  .cal-cell-year[data-disabled] {
    pointer-events: none;
    cursor: not-allowed;
    opacity: 0.3;
  }

  /* ── Reduced motion ──────────────────────────────────────────────────────── */

  @media (prefers-reduced-motion: reduce) {
    .field,
    .nav-btn,
    .cal-label-btn,
    .cal-cell {
      transition: none;
    }
  }
}
